import { createBrowserRouter, type RouteObject } from 'react-router-dom';
import type { AppSurface } from '@/lib/backend/config';

/**
 * One codebase, two sites (spec 2026-09-21 host split). Vite replaces every static
 * `import.meta.env.VITE_APP_SURFACE` occurrence with the build's literal mode string before
 * Rollup ever sees it — so the ternary below folds to `'admin' === 'portal'` (or
 * `'portal' === 'portal'`) at build time, and Rollup drops the untaken branch's dynamic import and
 * its whole chunk graph: the portal bundle never contains the admin panel and vice versa
 * (scripts/check-bundles.mjs enforces it). This only works inline, with no local/const/parameter
 * indirection between the literal comparison and the `import()` calls — routing the surface
 * through a variable (as this used to) defeats the fold, since Rollup doesn't constant-propagate
 * across module/parameter boundaries.
 */
async function loadSurfaceRoutes(): Promise<RouteObject[]> {
  return import.meta.env.VITE_APP_SURFACE === 'portal'
    ? (await import('./routes/portal-routes')).portalRoutes
    : (await import('./routes/admin-routes')).adminRoutes;
}

// Test-only: lets router.test.tsx assert on both route tables by name. Nothing in the production
// entry graph (main.tsx → createAppRouter → loadSurfaceRoutes) imports this, so Rollup drops it —
// and both dynamic imports inside it — from both builds same as any other unused export.
export async function routesFor(surface: AppSurface): Promise<RouteObject[]> {
  if (surface === 'portal') return (await import('./routes/portal-routes')).portalRoutes;
  return (await import('./routes/admin-routes')).adminRoutes;
}

export async function createAppRouter() {
  // Opt into React Router v7 routing behaviour now (v7_startTransition is set on RouterProvider).
  return createBrowserRouter(await loadSurfaceRoutes(), { future: { v7_relativeSplatPath: true } });
}
